import React, { useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'

const MEMED_SCRIPT_SRC = process.env.REACT_APP_MEMED_SCRIPT_SRC

const MEMED_SCRIPT_ID = 'memed-sinapse-prescricao'
const MEMED_STYLE_ID = 'memed-sinapse-prescricao-style'
const MEMED_BODY_CLASS = 'memed-prescription-open'
const MEMED_MODULE = 'plataforma.prescricao'

const MEMED_MODAL_CSS = `
  #memed-frame,
  iframe#memed-frame,
  iframe[src*="memed.com.br"] {
    position: fixed !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    width: min(100%, 95vw) !important;
    height: min(100%, 95vh) !important;
    max-width: 95vw !important;
    max-height: 95vh !important;
    border-radius: 12px !important;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35) !important;
    background: #fff !important;
    z-index: 99999 !important;
  }

  body.${MEMED_BODY_CLASS}::before {
    content: '';
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 99998;
    pointer-events: none;
  }
`

declare global {
  interface Window {
    MdSinapsePrescricao?: {
      event: {
        add: (
          event: string,
          callback: (module: { name: string; moduleName: string }) => void
        ) => void
      }
    }
    MdHub?: {
      module: {
        show: (module: string) => void
        hide?: (module: string) => void
      }
      command: {
        send: (
          module: string,
          command: string,
          payload?: unknown
        ) => Promise<unknown>
      }
      event?: {
        add: (event: string, callback: (data: unknown) => void) => void
      }
    }
  }
}

export type MemedPatient = {
  idExterno: string
  nome: string
  cpf?: string
  data_nascimento?: string
  endereco?: string
  cidade?: string
  telefone?: string
  nome_social?: string
}

type MemedProps = {
  token?: string
  patient?: MemedPatient
  visible?: boolean
  onPrescricaoSalva?: (prescriptionId: string, rawData?: unknown) => void
  onModuleReady?: () => void
  onClose?: () => void
}

const Memed = ({
  token,
  patient,
  visible,
  onPrescricaoSalva,
  onModuleReady,
  onClose,
}: MemedProps) => {
  const loadedRef = useRef(false)
  const patientRef = useRef(patient)
  const onPrescricaoSalvaRef = useRef(onPrescricaoSalva)
  const onModuleReadyRef = useRef(onModuleReady)
  const onCloseRef = useRef(onClose)
  const memedToken = token || process.env.REACT_APP_MEMED_TOKEN

  useEffect(() => {
    patientRef.current = patient
  }, [patient])
  useEffect(() => {
    onPrescricaoSalvaRef.current = onPrescricaoSalva
  }, [onPrescricaoSalva])
  useEffect(() => {
    onModuleReadyRef.current = onModuleReady
  }, [onModuleReady])
  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    if (visible) {
      document.body.classList.add(MEMED_BODY_CLASS)
    } else {
      document.body.classList.remove(MEMED_BODY_CLASS)
    }
  }, [visible])

  useEffect(() => {
    if (!memedToken) {
      console.error(
        '[Memed] token ausente. Defina REACT_APP_MEMED_TOKEN ou passe via prop.'
      )
      return
    }

    if (!MEMED_SCRIPT_SRC) {
      console.error(
        '[Memed] script src ausente. Defina REACT_APP_REACT_APP_MEMED_SCRIPT_SRC.'
      )
      return
    }

    if (!document.getElementById(MEMED_STYLE_ID)) {
      const style = document.createElement('style')
      style.id = MEMED_STYLE_ID
      style.innerHTML = MEMED_MODAL_CSS
      document.head.appendChild(style)
    }

    if (window.MdHub && window.MdSinapsePrescricao) {
      loadedRef.current = true
      const currentPatient = patientRef.current
      if (currentPatient) {
        window.MdHub.command.send(MEMED_MODULE, 'setPaciente', currentPatient)
      }
      onModuleReadyRef.current?.()
      return
    }

    if (loadedRef.current || document.getElementById(MEMED_SCRIPT_ID)) {
      return
    }

    const script = document.createElement('script')
    script.id = MEMED_SCRIPT_ID
    script.type = 'text/javascript'
    script.src = MEMED_SCRIPT_SRC
    script.dataset['token'] = memedToken
    script.async = true

    script.onload = () => {
      loadedRef.current = true

      window.MdSinapsePrescricao?.event.add('core:moduleInit', (module) => {
        if (module.name !== MEMED_MODULE) return

        const currentPatient = patientRef.current
        if (currentPatient) {
          window.MdHub?.command.send(
            MEMED_MODULE,
            'setPaciente',
            currentPatient
          )
        }

        window.MdHub?.event?.add('prescricaoImpressa', (data: any) => {
          onPrescricaoSalvaRef.current?.(data?.prescricao?.id, data)
        })

        onModuleReadyRef.current?.()
      })

      window.MdSinapsePrescricao?.event.add('core:moduleHide', (module) => {
        if (module.moduleName !== MEMED_MODULE) return
        document.body.classList.remove(MEMED_BODY_CLASS)
        onCloseRef.current?.()
      })
    }

    script.onerror = () => {
      console.error('[Memed] falha ao carregar script Sinapse.')
    }

    document.body.appendChild(script)

    return () => {
      document.body.classList.remove(MEMED_BODY_CLASS)
    }
  }, [memedToken])

  if (!visible || !onClose) return null

  return ReactDOM.createPortal(<div></div>, document.body)
}

export const openMemedPrescricao = () => {
  document.body.classList.add(MEMED_BODY_CLASS)
  window.MdHub?.module.show(MEMED_MODULE)
}

export const closeMemedPrescricao = () => {
  window.MdHub?.module.hide?.(MEMED_MODULE)
  document.body.classList.remove(MEMED_BODY_CLASS)
}

export default Memed
